import { type ReactNode, createContext, useCallback, useEffect } from "react";

import type {
  CreateLocalTrackOptions,
  ConnectOptions,
  LocalAudioTrack,
  LocalVideoTrack,
  Room,
  TwilioError,
  LocalTrack,
} from "twilio-video";

import type { ErrorCallback } from "../types";

// Import all hooks
import useLocalTracks from "./useLocalTracks";
import useMockLocalTracks from "./useMockLocalTracks";
import useMockRoom from "./useMockRoom";
import useMockScreenShareToggle from "./useMockScreenShareToggle";
import useRoom from "./useRoom";
import useScreenShareToggle from "./useScreenShareToggle";

// Conditionally use real or mock implementations
const useScreenShareToggleHook =
  import.meta.env.VITE_USE_MOCK_VIDEO === "true"
    ? useMockScreenShareToggle
    : useScreenShareToggle;

const useLocalTracksHook =
  import.meta.env.VITE_USE_MOCK_VIDEO === "true"
    ? useMockLocalTracks
    : useLocalTracks;

const useRoomHook =
  import.meta.env.VITE_USE_MOCK_VIDEO === "true" ? useMockRoom : useRoom;

export interface IVideoContext {
  room: Room | null;
  localTracks: (LocalAudioTrack | LocalVideoTrack)[];
  isConnecting: boolean;
  connect: (
    token: string,
    overrideTracks?: (LocalAudioTrack | LocalVideoTrack)[]
  ) => Promise<void>;
  onError: ErrorCallback;
  getLocalAudioTrack: () => Promise<LocalAudioTrack>;
  getLocalVideoTrack: (
    newOptions?: CreateLocalTrackOptions
  ) => Promise<LocalVideoTrack>;
  isAcquiringLocalTracks: boolean;
  removeLocalVideoTrack: () => void;
  isSharingScreen: boolean;
  toggleScreenShare: () => void;
  getAudioAndVideoTracks: () => Promise<LocalTrack[]>;
  removeLocalAudioTrack: () => void;
  toggleAudio: () => void;
  toggleVideo: () => void;
  disconnectFromRoom: () => void;
}

export const VideoContext = createContext<IVideoContext | null>(null);

interface VideoProviderProps {
  options?: ConnectOptions;
  onError: ErrorCallback;
  children: ReactNode;
}

export default function VideoProvider({
  options,
  children,
  onError = () => {},
}: VideoProviderProps) {
  const onErrorCallback: ErrorCallback = useCallback(
    (error) => {
      onError(error);
    },
    [onError]
  );

  const {
    localTracks,
    getLocalAudioTrack,
    getLocalVideoTrack,
    isAcquiringLocalTracks,
    removeLocalAudioTrack,
    removeLocalVideoTrack,
    getAudioAndVideoTracks,
  } = useLocalTracksHook();
  const { room, isConnecting, connect } = useRoomHook(
    localTracks,
    onErrorCallback,
    options
  );
  const [isSharingScreen, toggleScreenShare] = useScreenShareToggleHook(
    room,
    onError
  );

  // Add audio/video toggle and disconnect functions
  const toggleAudio = useCallback(() => {
    const audioTrack = localTracks.find(
      (track: LocalTrack) => track.kind === "audio"
    );
    if (audioTrack) {
      // Unpublish and stop existing audio track
      if (room) {
        room.localParticipant.unpublishTrack(audioTrack);
      }
      removeLocalAudioTrack();
    } else {
      // Acquire and publish new audio track
      getLocalAudioTrack().then((newTrack: LocalAudioTrack) => {
        if (room) {
          room.localParticipant.publishTrack(newTrack);
        }
      });
    }
  }, [room, localTracks, removeLocalAudioTrack, getLocalAudioTrack]);

  const toggleVideo = useCallback(() => {
    const videoTrack = localTracks.find(
      (track: LocalTrack) => track.kind === "video"
    );
    if (videoTrack) {
      // Unpublish and stop existing video track
      if (room) {
        room.localParticipant.unpublishTrack(videoTrack);
      }
      removeLocalVideoTrack();
    } else {
      // Acquire and publish new video track
      getLocalVideoTrack().then((newTrack: LocalVideoTrack) => {
        if (room && newTrack) {
          room.localParticipant.publishTrack(newTrack);
        }
      });
    }
  }, [room, localTracks, removeLocalVideoTrack, getLocalVideoTrack]);

  const disconnectFromRoom = useCallback(() => {
    // Stop all local tracks before disconnecting
    localTracks.forEach((track: LocalAudioTrack | LocalVideoTrack) => {
      track.stop();
    });
    if (room) {
      room.disconnect();
    }
  }, [room, localTracks]);

  // Handle room disconnection
  useEffect(() => {
    if (room) {
      const onDisconnected = (_: Room, error?: TwilioError) => {
        if (error) {
          onErrorCallback(error);
        }

        removeLocalAudioTrack();
        removeLocalVideoTrack();
        if (isSharingScreen) {
          toggleScreenShare();
        }
      };

      room.on("disconnected", onDisconnected);
      return () => {
        room.off("disconnected", onDisconnected);
      };
    }
  }, [
    room,
    onErrorCallback,
    removeLocalAudioTrack,
    removeLocalVideoTrack,
    isSharingScreen,
    toggleScreenShare,
  ]);

  // Handle track publication failures
  useEffect(() => {
    if (room) {
      room.localParticipant.on("trackPublicationFailed", onErrorCallback);
      return () => {
        room.localParticipant.off("trackPublicationFailed", onErrorCallback);
      };
    }
  }, [room, onErrorCallback]);

  // Handle audio track device change
  const audioTrack = localTracks.find(
    (track: LocalAudioTrack | LocalVideoTrack) => track.kind === "audio"
  );
  useEffect(() => {
    const handleDeviceChange = () => {
      if (audioTrack?.mediaStreamTrack.readyState === "ended") {
        audioTrack.restart({});
      }
    };

    navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);

    return () => {
      navigator.mediaDevices.removeEventListener(
        "devicechange",
        handleDeviceChange
      );
    };
  }, [audioTrack]);

  return (
    <VideoContext.Provider
      value={{
        room,
        localTracks,
        isConnecting,
        connect,
        onError: onErrorCallback,
        getLocalAudioTrack,
        getLocalVideoTrack,
        isAcquiringLocalTracks,
        removeLocalVideoTrack,
        isSharingScreen,
        toggleScreenShare,
        getAudioAndVideoTracks,
        removeLocalAudioTrack,
        toggleAudio,
        toggleVideo,
        disconnectFromRoom,
      }}
    >
      {children}
    </VideoContext.Provider>
  );
}
